import { cn } from '../../utils/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

/**
 * The app's one shimmer block: a flat token fill with an opacity pulse.
 *
 * Deliberately NOT a gradient sweep. A `via-` stop lighter than the surface
 * reads as glare in both themes - near-white on the lavender card in light
 * mode, and well above `--card` in dark. The flat fill is what 31 of the 33
 * hand-rolled shimmers in `apps/web` already did before this existed.
 *
 * `bg-secondary` rather than `bg-muted`: those two tokens resolve to the SAME
 * value in both themes (see `--secondary` / `--muted` in the app's
 * globals.css), so it is a naming choice, not a colour one - and
 * `bg-secondary` is what the overwhelming majority of call sites wrote.
 * `dark:bg-white/10` overrides it in dark, where `--secondary` sits too close
 * to `--card` to read as a placeholder.
 *
 * `motion-reduce:animate-none` is baked in even though the app's globals.css
 * already kills every animation under `prefers-reduced-motion`. This package
 * ships no CSS of its own, so the primitive must not depend on a consumer
 * having imported that stylesheet in order to be accessible.
 *
 * Size it with `h-*`/`w-*` via `className`, and override the radius the same
 * way - `cn` resolves the default `rounded-sm` against a caller's
 * `rounded-full` rather than emitting both and letting stylesheet order pick
 * the winner.
 */
export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        'bg-secondary animate-pulse rounded-sm motion-reduce:animate-none dark:bg-white/10',
        className
      )}
      {...props}
    />
  );
}
